// ============================================================================
// Gallery photo categorization
//
// Each property photo carries its category label in its `title` field (the
// Gallery page derives its filter tabs from these — e.g. "Amenities",
// "Community"). The special label "Other" marks uncategorized junk photos
// (interior listing shots, floor-plan renders, stray uploads) that read badly
// as decorative imagery.
//
// GalleryPhotos already drops these from the gallery grid; this shared helper
// lets the Amenities sections apply the same rule so an "Other" photo never
// surfaces on a card back or a feature image slot.
// ============================================================================

interface CategorizedPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
}

/** True when a gallery photo is categorized as "Other" (case/space-insensitive). */
export function isOtherPhoto(title?: string | null): boolean {
  return (title || '').trim().toLowerCase() === 'other';
}

/** Drop every photo categorized as "Other" from a gallery list. */
export function excludeOtherPhotos<T extends CategorizedPhoto>(photos: T[]): T[] {
  return photos.filter((p) => !isOtherPhoto(p.title));
}

/**
 * The set of image URLs (both `image` and `original_url`) belonging to
 * "Other"-categorized photos, so a resolved/generated image can be tested for
 * membership and rejected.
 */
export function otherPhotoUrls(photos: CategorizedPhoto[]): Set<string> {
  const urls = new Set<string>();
  for (const p of photos) {
    if (!isOtherPhoto(p.title)) continue;
    const image = (p.image || '').toString();
    const original = (p.original_url || '').toString();
    if (image) urls.add(image);
    if (original) urls.add(original);
  }
  return urls;
}
